import React, { useState, useEffect, useCallback } from "react";
import { useMeeting } from "@videosdk.live/react-sdk";
import useVoiceActivatedRecording from "../../../../hooks/useVoiceActivatedRecording";
import useBotSpeech from "../../../../hooks/useBotSpeech";
import QuestionnaireComponent from "./QuestionnaireComponent";
import { AnimatePresence, motion } from "framer-motion";
import { notifyCandidateNotSpeaking } from "../../../../hooks/socketManager";
import MicIcon from "../../../../assets/images/mic-new.svg";
import { useMeetingAppContext } from "../../../../hooks/MeetingAppContextDef";
import { sideBarModes } from "../../../../utils/common";
import CloseChatIcon from "../../../../assets/images/hide-chat.svg";
import OpenChatIcon from "../../../../assets/images/chat.svg";
import EndIcon from "../../../../assets/icons/Bottombar/EndIcon";
import "./animations.css";
import { containsEndInterviewPhrases } from "../../../../utils/interviewPhrases";
import ProcessingOverlay from "./ProcessingOverlay";
import { useQuestionnaireEngine } from "../../../../hooks/useQuestionnaireEngine";
import ShareScreenIcon from "../../../../assets/images/share-screen.svg";
const UserVoiceRecorder = ({
  showCountdown = false,
  contentWidth,
  sideBarMode,
  activePanelSpeaker,
  lastKnownSpeaker,
  onActiveSpeakerChange,
  stopQARecording,
}) => {
  const { setSideBarMode } = useMeetingAppContext();
  const {
    isRecording,
    isReady,
    audioLevel,
    startRecording,
    stopAndSendRecording,
    discardAndRestartRecording,
    analyserRef,
    audioContextRef,
  } = useVoiceActivatedRecording({ isBot: false });

  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [lstReceived, setLstReceived] = useState(false);
  const spacebarTimerRef = React.useRef(null);
  const speechIntervalRef = React.useRef(null);
  const isRecordingRef = React.useRef(false);
  const micWasOnBeforeHeadphoneDisconnectRef = React.useRef(null);

  // Ref-based locks for synchronous mic/spacebar gating.
  // React state updates are async so rapid presses can slip through before
  // the re-render; these refs are checked AND set in the same tick.
  const waitingForBotResponseRef = React.useRef(false);
  const botCurrentlySpeakingRef = React.useRef(false);
  const isBotBusyRef = React.useRef(false);
  const silentDetectedRef = React.useRef(false);

  const botSpeakingFailsafeRef = React.useRef(null);
  const BOT_SPEAKING_FAILSAFE_MS = 20000;
  const [activeSpeakerName, setActiveSpeakerName] = useState(null);
  const [messageQueue, setMessageQueue] = useState([]);
  const messagePlayingRef = React.useRef(false);

  const [messageWaitQueueLength, setMessageWaitQueueLength] = useState(0);
  const [showLowVoiceWarning, setShowLowVoiceWarning] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      setMessageWaitQueueLength(e.detail.messageWaitQueue);
    };

    window.addEventListener("bot-wait-queue-status", handler);

    return () => window.removeEventListener("bot-wait-queue-status", handler);
  }, []);

  const clearSpacebarTimer = () => {
    if (spacebarTimerRef.current != null) {
      // try both so we are robust regardless of whether you used setTimeout or setInterval earlier
      clearInterval(spacebarTimerRef.current);
      clearTimeout(spacebarTimerRef.current);

      spacebarTimerRef.current = null;
    }
  };

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  useEffect(() => {
    const handleLST = () => {
      setLstReceived(true);
    };

    window.addEventListener("lst-received", handleLST);
    return () => {
      window.removeEventListener("lst-received", handleLST);
    };
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      document.querySelectorAll("h2").forEach((h2) => {
        if (h2.textContent?.includes("Error Code: 3016")) {
          const btn = h2.parentElement?.querySelector(
            ".mt-6.flex.justify-end > button:first-child",
          );

          if (btn) {
            btn.style.display = "none";
            clearInterval(interval); // ← only runs ONCE, ever
          }
        }
      });
    }, 1);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      document.querySelectorAll("p").forEach((p) => {
        if (p.textContent?.includes("Screen sharing permission denied.")) {
          const firstButton = p.nextElementSibling?.querySelector("button");

          if (firstButton) {
            firstButton.style.display = "none";
            // or firstButton.remove();
          }
        }
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);
  const { isBotSpeaking, isBotBusy, queueStatus } = useBotSpeech();
  const { localMicOn, toggleMic, toggleScreenShare, localScreenShareOn } =
    useMeeting();
  const [showHint, setShowHint] = useState(false);
  const [isLowBatteryAudioPlaying, setIsLowBatteryAudioPlaying] = useState(
    () => !!window.__isLowBatteryAudioPlaying,
  );
  const [waitingForBotResponse, setWaitingForBotResponse] = useState(false);
  const [showMuteWarning, setShowMuteWarning] = useState(false);
  const [acknowledgement, setAcknowledgement] = useState("");
  const [silentDetected, setSilentDetected] = useState(false);
  const localMicOnRef = React.useRef(localMicOn);
  useEffect(() => {
    const onStart = () => setIsLowBatteryAudioPlaying(true);
    const onEnd = () => setIsLowBatteryAudioPlaying(false);
    window.addEventListener("low-battery-audio-start", onStart);
    window.addEventListener("low-battery-audio-end", onEnd);
    return () => {
      window.removeEventListener("low-battery-audio-start", onStart);
      window.removeEventListener("low-battery-audio-end", onEnd);
    };
  }, []);
  useEffect(() => {
    localMicOnRef.current = localMicOn;
  }, [localMicOn]);
  useEffect(() => {
    isBotBusyRef.current = isBotBusy;
  }, [isBotBusy]);
  useEffect(() => {
    silentDetectedRef.current = silentDetected;
  }, [silentDetected]);

  // Questionnaire states
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [questionnaireQueue, setQuestionnaireQueue] = useState([]);
  const [usedQuestionIds, setUsedQuestionIds] = useState([]);

  // Animation states
  const [processorAnimating, setProcessorAnimating] = useState(false);
  const [questionnaireAnimating, setQuestionnaireAnimating] = useState(false);
  const [questionnaireVisible, setQuestionnaireVisible] = useState(false);

  // FIXED: Enhanced conversation state management
  const [conversationState, setConversationState] =
    useState("countdown_active");
  const [shouldShowQuestion, setShouldShowQuestion] = useState(false);

  // FIXED: Enhanced first message tracking
  const [firstBotMessageReceived, setFirstBotMessageReceived] = useState(false);
  const [firstBotAudioFinished, setFirstBotAudioFinished] = useState(false);
  const [waitingForIntroAudio, setWaitingForIntroAudio] = useState(false);

  // FIXED: Add bot speaking tracking
  const [botCurrentlySpeaking, setBotCurrentlySpeaking] = useState(false);
  const [botResponseComplete, setBotResponseComplete] = useState(false);

  // Add a flag to prevent multiple simultaneous question loads
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);

  // NEW: Pre-questionnaire audio states
  const [isPlayingPreQuestionnaireAudio, setIsPlayingPreQuestionnaireAudio] =
    useState(false);
  const [preQuestionnaireAudioRef, setPreQuestionnaireAudioRef] =
    useState(null);

  // FIXED: Add state to track if we're in questionnaire mode (includes pre-audio)
  const [questionnaireMode, setQuestionnaireMode] = useState(false);
  const [fbkReceived, setFbkReceived] = useState(false);

  // Questionnaire probability control
  const [questionnaireCount, setQuestionnaireCount] = useState(0);
  const [userResponseCount, setUserResponseCount] = useState(0);
  const [nextQuestionnaireAt, setNextQuestionnaireAt] = useState(null);
  const MIN_RESPONSES_BEFORE_FIRST_QUESTIONNAIRE = 2;
  const MIN_GAP_BETWEEN_QUESTIONNAIRES = 2;
  const MAX_GAP_BETWEEN_QUESTIONNAIRES = 5;

  // Pending bot responses queue for questionnaire blocking
  const [pendingBotResponses, setPendingBotResponses] = useState([]);

  const INTERVIEW_ID = sessionStorage.getItem("interviewId");
  const CACHE_KEY = `questionnaire_cache_${INTERVIEW_ID}`;
  const USED_QUESTIONS_KEY = `used_questions_${INTERVIEW_ID}`;

  // NEW: Pre-questionnaire audio file names
  const PRE_QUESTIONNAIRE_AUDIO_FILES = [
    "anushka_Sentence1.mp3",
    "anushka_Sentence2.mp3",
    "anushka_Sentence3.mp3",
    "anushka_Sentence4.mp3",
    "anushka_Sentence5.mp3",
    "anushka_Sentence6.mp3",
    "anushka_Sentence7.mp3",
    "anushka_Sentence8.mp3",
    "anushka_Sentence9.mp3",
    "anushka_Sentence10.mp3",
  ];
  useEffect(() => {
    if (onActiveSpeakerChange) {
      onActiveSpeakerChange(activeSpeakerName);
    }
  }, [activeSpeakerName, onActiveSpeakerChange]);
  useEffect(() => {
    const handleForceStopRecording = () => {
      if (isRecording) {
        stopAndSendRecording();
      }
    };

    const handleForceStopAllMedia = () => {
      if (preQuestionnaireAudioRef) {
        preQuestionnaireAudioRef.pause();
        preQuestionnaireAudioRef.currentTime = 0;
        setPreQuestionnaireAudioRef(null);
      }
    };

    window.addEventListener("force-stop-recording", handleForceStopRecording);
    window.addEventListener("force-stop-all-media", handleForceStopAllMedia);

    return () => {
      window.removeEventListener(
        "force-stop-recording",
        handleForceStopRecording,
      );
      window.removeEventListener(
        "force-stop-all-media",
        handleForceStopAllMedia,
      );
    };
  }, [isRecording, stopAndSendRecording, preQuestionnaireAudioRef]);

  useEffect(() => {
    // Clear silent detected when bot starts speaking
    if (botCurrentlySpeaking && silentDetected) {
      setSilentDetected(false);
    }
  }, [botCurrentlySpeaking, silentDetected]);

  // When the hook rejects audio due to no speech/too short, reset the waiting
  // lock and show a user-facing warning so the UI doesn't get stuck.
  useEffect(() => {
    const handleRejected = () => {
      setWaitingForBotResponse(false);
      waitingForBotResponseRef.current = false;
      setBotCurrentlySpeaking(false);
      botCurrentlySpeakingRef.current = false;
      showSpeechWarning(
        "No speech detected. Please speak clearly into your microphone.",
      );
    };
    window.addEventListener("recording-rejected-no-speech", handleRejected);
    return () =>
      window.removeEventListener(
        "recording-rejected-no-speech",
        handleRejected,
      );
  }, []);

  // FIXED: Update questionnaire mode tracking
  // NOTE: This used to be derived via useEffect + setState, which meant the
  // flag lagged one render behind showQuestionnaire/isPlayingPreQuestionnaireAudio.
  // That lag created a race: if a bot-response event arrived in the gap between
  // showQuestionnaire flipping to false and this effect catching up,
  // handleBotResponse would still see questionnaireMode === true, stash the
  // response in pendingBotResponses, and nothing would ever flush it again —
  // leaving waitingForBotResponse stuck true and the "Processing your
  // response" overlay spinning forever.
  // A ref updated inline during render has zero lag, so we use that as the
  // source of truth for the actual gating check in handleBotResponse.
  const questionnaireModeRef = React.useRef(false);
  questionnaireModeRef.current =
    showQuestionnaire || isPlayingPreQuestionnaireAudio;
  window.__questionnaireMode = questionnaireModeRef.current;

  useEffect(() => {
    setQuestionnaireMode(showQuestionnaire || isPlayingPreQuestionnaireAudio);
  }, [showQuestionnaire, isPlayingPreQuestionnaireAudio]);
  useEffect(() => {
    if (currentQuestion) {
      setAcknowledgement(getRandomAcknowledgement());
    }
  }, [currentQuestion]);
  useEffect(() => {
    const handleHeadphoneDisconnect = () => {
      console.log("🎧 HEADPHONE DISCONNECTED");

      // Edge can mute VideoSDK microphone asynchronously.
      // Give VideoSDK/Edge time to update localMicOn.
      setTimeout(() => {
        console.log("🎤 VideoSDK mic state after headphone disconnect:", {
          localMicOn: localMicOnRef.current,
        });

        if (!localMicOnRef.current) {
          console.log(
            "🔊 VideoSDK mic is muted after headphone disconnect. Calling toggleMic()...",
          );

          try {
            toggleMic();
          } catch (error) {
            console.error("❌ Failed to unmute VideoSDK microphone:", error);
          }
        }
      }, 1000);
    };

    window.addEventListener(
      "headphone-disconnected",
      handleHeadphoneDisconnect,
    );

    return () => {
      window.removeEventListener(
        "headphone-disconnected",
        handleHeadphoneDisconnect,
      );
    };
  }, [toggleMic]);

  // NEW: Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      if (preQuestionnaireAudioRef) {
        preQuestionnaireAudioRef.pause();
        preQuestionnaireAudioRef.currentTime = 0;
      }
    };
  }, [preQuestionnaireAudioRef]);

  // FIXED: Handle countdown completion - start waiting for intro
  useEffect(() => {
    const handleCountdownComplete = () => {
      setConversationState("waiting_for_intro");
      setWaitingForIntroAudio(true);
      setShowHint(true); // Show "Hello and Good [time]! Anushka from our side..."

      // ADDED: Trigger the bot's introductory message after showing the waiting message
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("trigger-bot-intro", {
            detail: {
              message: "Please introduce yourself and start the interview.",
            },
          }),
        );
      }, 2000); // 2 second delay to show the greeting message first
    };

    window.addEventListener("countdown-complete", handleCountdownComplete);
    return () => {
      window.removeEventListener("countdown-complete", handleCountdownComplete);
    };
  }, []);
  useEffect(() => {
    const handleFBK = () => {
      setFbkReceived(true);

      setShouldShowQuestion(false);
    };

    window.addEventListener("fbk-received", handleFBK);
    return () => window.removeEventListener("fbk-received", handleFBK);
  }, []);
  const {
    loadQuestionsToCache,
    getNextQuestion,
    markQuestionAsUsed,
    submitAnswerToAPI,
    isLoadingQuestions,
  } = useQuestionnaireEngine({ interviewId: INTERVIEW_ID });
  // WATCHDOG: waitingForBotResponse should always get cleared by a real
  // bot-response event. If it doesn't (dropped event, backend hiccup, or any
  // future race we haven't seen yet), this force-clears it after 15s so the
  // "Processing your response" overlay can never hang indefinitely.
  useEffect(() => {
    if (!waitingForBotResponse) return undefined;

    const watchdog = setTimeout(() => {
      console.warn("waitingForBotResponse stuck true for 15s — force clearing");
      setWaitingForBotResponse(false);
      waitingForBotResponseRef.current = false;
    }, 15000);

    return () => clearTimeout(watchdog);
  }, [waitingForBotResponse]);

  // Processor animation effect
  useEffect(() => {
    if (
      waitingForBotResponse &&
      !showQuestionnaire &&
      !questionnaireVisible &&
      !isPlayingPreQuestionnaireAudio
    ) {
      setProcessorAnimating(true);
    } else {
      const timer = setTimeout(() => {
        setProcessorAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [
    waitingForBotResponse,
    showQuestionnaire,
    questionnaireVisible,
    isPlayingPreQuestionnaireAudio,
  ]);
  // Ref-based cooldown: once the candidate says something matching
  // containsEndInterviewPhrases, we suppress the questionnaire for the
  // next N candidate responses (decremented in handleUserTranscription).
  // Using a ref (not state) so the decrement/read inside the event
  // handler below stays synchronous and doesn't depend on stale closures.
  const endPhraseCooldownRef = React.useRef(0);
  const END_PHRASE_QUESTIONNAIRE_SUPPRESS_COUNT = 3;

  // Function to decide whether to show questionnaire
  const shouldShowQuestionnaire = () => {
    if (fbkReceived || window.__FBK_RECEIVED__) return false;
    if (endPhraseCooldownRef.current > 0) return false;
    if (nextQuestionnaireAt === null) return false;
    return userResponseCount >= nextQuestionnaireAt;
  };
  useEffect(() => {
    const finalizing =
      showQuestionnaire ||
      isPlayingPreQuestionnaireAudio ||
      questionnaireVisible ||
      pendingBotResponses.length > 0;

    window.dispatchEvent(
      new CustomEvent("questionnaire-mode-changed", {
        detail: { active: finalizing },
      }),
    );
  }, [
    showQuestionnaire,
    isPlayingPreQuestionnaireAudio,
    questionnaireVisible,
    pendingBotResponses.length,
  ]);
  // Function to schedule the next questionnaire
  const scheduleNextQuestionnaire = () => {
    const gap =
      MIN_GAP_BETWEEN_QUESTIONNAIRES +
      Math.floor(
        Math.random() *
          (MAX_GAP_BETWEEN_QUESTIONNAIRES - MIN_GAP_BETWEEN_QUESTIONNAIRES + 1),
      );

    const nextAt = userResponseCount + gap;
    setNextQuestionnaireAt(nextAt);
  };

  // Function to play all pending bot responses
  const playAllPendingBotResponses = () => {
    if (pendingBotResponses.length > 0) {
      pendingBotResponses.forEach((response, index) => {
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("bot-response", { detail: response }),
          );
        }, index * 100);
      });

      setPendingBotResponses([]);
    }
  };

  // SAFETY NET: If any bot response ever ends up queued in pendingBotResponses
  // while we're not actually in questionnaire mode (e.g. a rare race slips
  // past the ref-based guard above), this flushes it automatically instead of
  // leaving waitingForBotResponse stuck true forever.
  useEffect(() => {
    if (
      !showQuestionnaire &&
      !isPlayingPreQuestionnaireAudio &&
      pendingBotResponses.length > 0
    ) {
      const t = setTimeout(() => {
        playAllPendingBotResponses();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [showQuestionnaire, isPlayingPreQuestionnaireAudio, pendingBotResponses]);

  // NEW: Function to play random pre-questionnaire audio
  const playRandomPreQuestionnaireAudio = () => {
    return new Promise((resolve, reject) => {
      try {
        // Select random audio file
        const randomIndex = Math.floor(
          Math.random() * PRE_QUESTIONNAIRE_AUDIO_FILES.length,
        );
        const selectedAudioFile = PRE_QUESTIONNAIRE_AUDIO_FILES[randomIndex];
        const audioPath = `/sounds/${selectedAudioFile}`;

        // Create audio element
        const audio = new Audio(audioPath);
        setPreQuestionnaireAudioRef(audio);
        setIsPlayingPreQuestionnaireAudio(true);

        // Set up event listeners
        audio.onloadeddata = () => {};

        audio.onplay = () => {
          // Show hint that Anushka is speaking
          setShowHint(true);
        };

        audio.onended = () => {
          setIsPlayingPreQuestionnaireAudio(false);
          setPreQuestionnaireAudioRef(null);
          setShowHint(false);
          resolve();
        };

        audio.onerror = (error) => {
          console.error(`Error playing audio ${selectedAudioFile}:`, error);
          setIsPlayingPreQuestionnaireAudio(false);
          setPreQuestionnaireAudioRef(null);
          setShowHint(false);
          reject(error);
        };

        // Play the audio
        audio.play().catch((error) => {
          console.error(`Failed to play audio ${selectedAudioFile}:`, error);
          setIsPlayingPreQuestionnaireAudio(false);
          setPreQuestionnaireAudioRef(null);
          setShowHint(false);
          reject(error);
        });
      } catch (error) {
        console.error("Error in playRandomPreQuestionnaireAudio:", error);
        setIsPlayingPreQuestionnaireAudio(false);
        setPreQuestionnaireAudioRef(null);
        setShowHint(false);
        reject(error);
      }
    });
  };

  // Questionnaire animation effect
  useEffect(() => {
    if (showQuestionnaire) {
      if (processorAnimating) {
        setProcessorAnimating(false);
      }

      setQuestionnaireVisible(true);
      setTimeout(() => {
        setQuestionnaireAnimating(true);
      }, 50);
    } else {
      setQuestionnaireAnimating(false);
      const timer = setTimeout(() => {
        setQuestionnaireVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showQuestionnaire]);

  // Initialize cache and used questions from sessionStorage
  useEffect(() => {
    const cachedQuestions = sessionStorage.getItem(CACHE_KEY);
    const cachedUsedQuestions = sessionStorage.getItem(USED_QUESTIONS_KEY);

    if (cachedQuestions) {
      setQuestionnaireQueue(JSON.parse(cachedQuestions));
    }

    if (cachedUsedQuestions) {
      setUsedQuestionIds(JSON.parse(cachedUsedQuestions));
    }

    if (!nextQuestionnaireAt) {
      const firstQuestionnaireAt =
        MIN_RESPONSES_BEFORE_FIRST_QUESTIONNAIRE +
        Math.floor(
          Math.random() *
            (MAX_GAP_BETWEEN_QUESTIONNAIRES -
              MIN_GAP_BETWEEN_QUESTIONNAIRES +
              1),
        );
      setNextQuestionnaireAt(firstQuestionnaireAt);
    }
  }, []);

  // Load questions on component mount
  useEffect(() => {
    loadQuestionsToCache();
  }, []);

  // Check for mute status while recording
  useEffect(() => {
    let muteCheckInterval;

    if (isRecording) {
      muteCheckInterval = setInterval(() => {
        if (!localMicOn) {
          setShowMuteWarning(true);
        } else {
          setShowMuteWarning(false);
        }
      }, 500);
    } else {
      setShowMuteWarning(false);
    }

    return () => {
      if (muteCheckInterval) {
        clearInterval(muteCheckInterval);
      }
    };
  }, [isRecording, localMicOn]);

  // MODIFIED: Show question when shouldShowQuestion is true - now with pre-questionnaire audio
  useEffect(() => {
    if (window.__FBK_RECEIVED__ || fbkReceived) {
      return;
    }
    if (
      shouldShowQuestion &&
      !isQuestionLoading &&
      !showQuestionnaire &&
      !isPlayingPreQuestionnaireAudio
    ) {
      const showQuestion = async () => {
        setIsQuestionLoading(true);
        setShouldShowQuestion(false);

        try {
          const nextQuestion = await getNextQuestion();
          if (nextQuestion) {
            // NEW: Play random pre-questionnaire audio first
            //  Show the questionnaire first, then play audio
            setCurrentQuestion(nextQuestion);
            setQuestionStartTime(Date.now());
            setShowQuestionnaire(true);
            setQuestionnaireCount((prev) => prev + 1);

            try {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              await playRandomPreQuestionnaireAudio();
            } catch (audioError) {
              console.warn(
                "Failed to play pre-questionnaire audio:",
                audioError,
              );
            }
          } else {
          }
        } catch (error) {
          console.error("Error loading question:", error);
        } finally {
          setIsQuestionLoading(false);
        }
      };

      showQuestion();
    }
  }, [
    shouldShowQuestion,
    isQuestionLoading,
    showQuestionnaire,
    questionnaireQueue,
    isPlayingPreQuestionnaireAudio,
  ]);

  // FIXED: Main event handlers with proper bot speaking tracking
  useEffect(() => {
    const handleUserTranscription = async (event) => {
      if (speechIntervalRef.current) {
        clearInterval(speechIntervalRef.current);
        speechIntervalRef.current = null;
      }

      const newResponseCount = userResponseCount + 1;
      setUserResponseCount(newResponseCount);

      // Count this response against any active end-phrase cooldown, so the
      // questionnaire stays suppressed for exactly the next N candidate
      // responses after containsEndInterviewPhrases fires (see below).
      if (endPhraseCooldownRef.current > 0) {
        endPhraseCooldownRef.current -= 1;
      }

      if (
        isQuestionLoading ||
        showQuestionnaire ||
        isPlayingPreQuestionnaireAudio
      ) {
        return;
      }

      // Only allow user speech after first bot audio is done
      if (conversationState !== "ready_for_user") {
        return;
      }

      // Set waiting for bot response and hide hint
      setWaitingForBotResponse(true);
      waitingForBotResponseRef.current = true;
      setShowHint(false);
      setBotResponseComplete(false); // FIXED: Reset bot response state

      if (!fbkReceived && !window.__FBK_RECEIVED__) {
        // Check every candidate response for end-interview phrases (not
        // just ones where a questionnaire happened to be due), so the
        // cooldown gets (re)armed promptly regardless of scheduling.
        const wantsToEndInterview = await containsEndInterviewPhrases(
          event.detail?.transcript,
        );
        // alert("new" + wantsToEndInterview);

        if (wantsToEndInterview) {
          // Candidate said something matching an end-interview phrase —
          // hold off on the questionnaire for the next few responses
          // instead of just this one.
          endPhraseCooldownRef.current =
            END_PHRASE_QUESTIONNAIRE_SUPPRESS_COUNT;
        } else if (shouldShowQuestionnaire()) {
          setShouldShowQuestion(true);
          scheduleNextQuestionnaire();
        }
      }
    };

    // FIXED: Bot response handler - prevent processing during questionnaire mode
    const handleBotResponse = (event) => {
      const { transcript, audioBlob, speakerName } = event.detail;

      // FIXED: Check questionnaire mode via ref (synchronous, no lag) instead
      // of the questionnaireMode state, which used to update a render late.
      if (questionnaireModeRef.current) {
        // Store the response for later playback
        const { transcript, audioBlob, audioFormat } = event.detail;
        if (transcript !== "EOF") {
          setPendingBotResponses((prev) => [...prev, event.detail]);
        }

        event.stopImmediatePropagation();
        event.preventDefault();
        return false; // Explicitly return false to block further processing
      }

      setWaitingForBotResponse(false);
      waitingForBotResponseRef.current = false;
      setBotCurrentlySpeaking(true);
      botCurrentlySpeakingRef.current = true; // FIXED: Bot is about to start speaking
      setShowHint(false); // FIXED: Hide any hints when bot starts responding

      // Arm the failsafe: if "bot-finished-speaking" doesn't arrive within
      // BOT_SPEAKING_FAILSAFE_MS, force the flags back off so the UI can't
      // wedge permanently (see comment on botSpeakingFailsafeRef above).
      if (botSpeakingFailsafeRef.current) {
        clearTimeout(botSpeakingFailsafeRef.current);
      }
      botSpeakingFailsafeRef.current = setTimeout(() => {
        if (botCurrentlySpeakingRef.current) {
          console.warn(
            "[UserVoiceRecorder] bot-finished-speaking never arrived — " +
              "forcing botCurrentlySpeaking/waitingForBotResponse back to " +
              "false so the user isn't locked out.",
          );
          setBotCurrentlySpeaking(false);
          botCurrentlySpeakingRef.current = false;
          setWaitingForBotResponse(false);
          waitingForBotResponseRef.current = false;
        }
        botSpeakingFailsafeRef.current = null;
      }, BOT_SPEAKING_FAILSAFE_MS);

      // FIXED: Handle first intro message
      if (waitingForIntroAudio && !firstBotMessageReceived) {
        setFirstBotMessageReceived(true);
        setWaitingForIntroAudio(false);
        setShowHint(false); // Hide "waiting for intro" message
        // return;
      }
    };

    const handleBotFinishedSpeaking = () => {
      // Normal path completed successfully — disarm the failsafe so it
      // doesn't fire later and stomp on a subsequent bot-response cycle.
      if (botSpeakingFailsafeRef.current) {
        clearTimeout(botSpeakingFailsafeRef.current);
        botSpeakingFailsafeRef.current = null;
      }

      setBotCurrentlySpeaking(false);
      botCurrentlySpeakingRef.current = false;

      const isFirstIntroCompletion =
        firstBotMessageReceived && !firstBotAudioFinished;

      // mark intro completed
      if (isFirstIntroCompletion) {
        setFirstBotAudioFinished(true);
      }

      setTimeout(() => {
        setConversationState("ready_for_user");
        setShowHint(true);
      }, 100);

      // start/restart silence reminder timer
      clearSpacebarTimer();

      spacebarTimerRef.current = setInterval(() => {
        console.warn("Candidate silent for 30 seconds");

        if (!isRecordingRef.current) {
          // Show "Silent Detected" hint and post to websocket
          setSilentDetected(true);
          notifyCandidateNotSpeaking();
          // Will clear silentDetected automatically when bot starts speaking (botCurrentlySpeaking becomes true)
        }
      }, 30000);
    };

    // FIXED: Bot queue empty handler - backup to ensure hint shows
    const handleBotQueueEmpty = () => {
      // FIXED: Ensure hint shows when queue is empty
      // if (
      //   firstBotAudioFinished &&
      //   conversationState === "ready_for_user" &&
      //   !showQuestionnaire &&
      //   !botCurrentlySpeaking &&
      //   !waitingForBotResponse &&
      //   !isPlayingPreQuestionnaireAudio
      // ) {
      //   setBotResponseComplete(true);
      //   setShowHint(true);
      // }
    };

    // Handle explicit questionnaire trigger
    const handleShowQuestionnaire = async (event) => {
      if (
        isQuestionLoading ||
        showQuestionnaire ||
        isPlayingPreQuestionnaireAudio
      ) {
        return;
      }

      if (event.detail && event.detail.question) {
        // NEW: Play pre-questionnaire audio even for explicit triggers
        try {
          await playRandomPreQuestionnaireAudio();
        } catch (audioError) {
          console.warn(
            "Pre-questionnaire audio failed for explicit trigger:",
            audioError,
          );
        }

        setCurrentQuestion(event.detail.question);
        setShowQuestionnaire(true);
        setQuestionnaireCount((prev) => prev + 1);
      } else {
        setShouldShowQuestion(true);
      }
    };

    // FIXED: Use capture phase to intercept bot-response events before they reach useBotSpeech
    window.addEventListener("user-transcription", handleUserTranscription);
    window.addEventListener("bot-response", handleBotResponse, true); // Use capture phase
    window.addEventListener("bot-finished-speaking", handleBotFinishedSpeaking);
    window.addEventListener("bot-queue-empty", handleBotQueueEmpty);
    window.addEventListener("show-questionnaire", handleShowQuestionnaire);

    return () => {
      window.removeEventListener("user-transcription", handleUserTranscription);
      window.removeEventListener("bot-response", handleBotResponse, true); // Remove capture phase
      window.removeEventListener(
        "bot-finished-speaking",
        handleBotFinishedSpeaking,
      );
      window.removeEventListener("bot-queue-empty", handleBotQueueEmpty);
      window.removeEventListener("show-questionnaire", handleShowQuestionnaire);
      if (botSpeakingFailsafeRef.current) {
        clearTimeout(botSpeakingFailsafeRef.current);
        botSpeakingFailsafeRef.current = null;
      }
    };
  }, [
    conversationState,
    showQuestionnaire,
    isQuestionLoading,
    userResponseCount,
    nextQuestionnaireAt,
    firstBotMessageReceived,
    firstBotAudioFinished,
    waitingForIntroAudio,
    botCurrentlySpeaking,
    waitingForBotResponse,
    isPlayingPreQuestionnaireAudio,
    questionnaireMode, // Add this dependency
  ]);
  useEffect(() => {
    const LONG_PRESS_MS = 600;

    const onKeyDown = (e) => {
      if (e.code !== "Space") return;

      // ignore if typing in input
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        showQuestionnaire ||
        isQuestionLoading ||
        isPlayingPreQuestionnaireAudio
      ) {
        return;
      }

      // prevent repeat firing when key is held
      if (e.repeat) return;

      spacebarDownAtRef.current = Date.now();
      spacebarWasLongPressedRef.current = false;

      if (spacebarLongPressTimerRef.current)
        clearTimeout(spacebarLongPressTimerRef.current);

      spacebarLongPressTimerRef.current = setTimeout(() => {
        // If still holding, show warning
        spacebarWasLongPressedRef.current = true;
        setShowSpacebarLongPressWarning(true);

        // auto-hide after 2 seconds
        setTimeout(() => setShowSpacebarLongPressWarning(false), 10000);
      }, LONG_PRESS_MS);
    };

    const onKeyUp = (e) => {
      if (e.code !== "Space") return;

      if (spacebarLongPressTimerRef.current) {
        clearTimeout(spacebarLongPressTimerRef.current);
        spacebarLongPressTimerRef.current = null;
      }

      // if it was long-pressed, don't do anything extra here
      // your main logic already runs on keydown (handleSpacebarPress)
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);

      if (spacebarLongPressTimerRef.current) {
        clearTimeout(spacebarLongPressTimerRef.current);
        spacebarLongPressTimerRef.current = null;
      }
    };
  }, [showQuestionnaire, isQuestionLoading, isPlayingPreQuestionnaireAudio]);
  useEffect(() => {
    return () => {
      if (speechIntervalRef.current) {
        clearInterval(speechIntervalRef.current);
        speechIntervalRef.current = null;
      }
      if (spacebarTimerRef.current) {
        clearInterval(spacebarTimerRef.current);
        spacebarTimerRef.current = null;
      }
    };
  }, []);
  const silenceTimerRef = React.useRef(null);

  // ─── Refs used inside handleSpacebarPress ────────────────────────────────
  // Declared here (before the useCallback) so the closure captures the real
  // ref objects instead of an uninitialised `let` binding, which was causing
  // hasSpeechDetectedRef.current to always be undefined and the
  // "No speech detected" warning to never appear.
  const audioLevelRef = React.useRef(0);

  // Tracks when recording started (to enforce min duration)
  const recordingStartedAtRef = React.useRef(null);
  // Tracks whether meaningful audio was detected during recording
  const hasSpeechDetectedRef = React.useRef(false);

  const [showPressSpacebarWarning, setShowPressSpacebarWarning] =
    useState(false);

  const [showSpacebarLongPressWarning, setShowSpacebarLongPressWarning] =
    useState(false);

  // Warning shown when user stops recording too quickly or without speaking
  const [showTooShortWarning, setShowTooShortWarning] = useState(false);
  const tooShortWarningTimerRef = React.useRef(null);

  const showSpeechWarning = (msg) => {
    setShowTooShortWarning(msg);
    if (tooShortWarningTimerRef.current)
      clearTimeout(tooShortWarningTimerRef.current);
    tooShortWarningTimerRef.current = setTimeout(
      () => setShowTooShortWarning(false),
      3000,
    );
  };

  const spacebarDownAtRef = React.useRef(null);
  const spacebarLongPressTimerRef = React.useRef(null);
  const spacebarWasLongPressedRef = React.useRef(false);

  // Speech detector refs — declared here so handleSpacebarPress closure captures them
  const speechDetectIntervalRef = React.useRef(null);
  const speechBaselineRef = React.useRef(null);
  const speechSustainStartRef = React.useRef(null);
  const noiseCalibIntervalRef = React.useRef(null); // passive pre-calibration interval
  const noiseCalibSamplesRef = React.useRef([]); // rolling noise samples while idle

  // Speech detector constants
  // No BASELINE_WINDOW_MS needed — baseline is pre-calibrated passively while idle.
  // SPEECH_MARGIN is intentionally low (0.1) so soft voices only need to be
  // slightly above the room noise floor to be detected.
  const SPEECH_MARGIN = 0.1;
  const MIN_SPEECH_THRESHOLD = 0.04; // absolute floor for dead-silent rooms
  const SUSTAINED_MS = 200; // ms of continuous speech needed to confirm
  const SUSTAIN_GRACE_MS = 80; // brief dips allowed without resetting streak

  // Helper: read live RMS directly from the Web Audio AnalyserNode.
  // This bypasses React state entirely — no lag, no dropped frames —
  // which is critical for detecting low-volume voices.
  const readRawRMS = useCallback(() => {
    const analyser = analyserRef?.current;
    if (!analyser) return 0;
    const buf = new Uint8Array(analyser.fftSize);

    analyser.getByteTimeDomainData(buf);
    const rms = Math.sqrt(
      buf.reduce((sum, v) => sum + (v - 128) ** 2, 0) / buf.length,
    );
    return Math.min(rms / 10, 10);
  }, [analyserRef]);

  // Helper: read perceived voice volume using Frequency Data (0-255 scale).
  // This is vastly superior to raw RMS for detecting low vs high voices
  // because it isolates human speech frequencies and scales linearly with loudness.
  // It completely ignores low-frequency hums and DC offsets which cause fake "peaks".
  const readVoiceVolume = useCallback(() => {
    const analyser = analyserRef?.current;
    if (!analyser) return 0;

    const buf = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(buf);

    // Human voice fundamental frequencies and main harmonics typically
    // fall between ~85Hz and ~3000Hz.
    const minBin = 2;
    const maxBin = 64;

    let sum = 0;
    let count = 0;
    for (let i = minBin; i <= maxBin && i < buf.length; i++) {
      sum += buf[i];
      count++;
    }
    return count > 0 ? sum / count : 0;
  }, [analyserRef]);

  // ─────────────────────────────────────────────────────────────────────────

  const handleSpacebarPress = useCallback(
    (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.closest?.(".monaco-editor") ||
        showQuestionnaire ||
        isQuestionLoading ||
        isPlayingPreQuestionnaireAudio
      ) {
        return;
      }

      if (event.code === "Space") {
        if (event.repeat) return;
        event.preventDefault();

        // ✅ Spacebar works in TWO cases:
        // 1. When showing green "Press SPACEBAR to START speaking" → conversationState === "ready_for_user" && !lstReceived
        // 2. When showing yellow "Press SPACEBAR or STOP when done speaking" → isRecording

        const canStartRecording =
          conversationState === "ready_for_user" &&
          !lstReceived &&
          !isRecording;
        const canStopRecording = isRecording;

        if (!canStartRecording && !canStopRecording) {
          return;
        }

        if (!isReady) return;
        if (isBotBusyRef.current) return;
        if (waitingForBotResponseRef.current) return;
        if (botCurrentlySpeakingRef.current) return;
        if (silentDetectedRef.current) return;
        setShowPressSpacebarWarning(false);

        //  USER FINISHED SPEAKING or STARTS SPEAKING
        if (isRecording) {
          // Validate: minimum duration 500ms AND meaningful audio detected
          const MIN_RECORDING_MS = 500;
          const elapsed = recordingStartedAtRef.current
            ? Date.now() - recordingStartedAtRef.current
            : MIN_RECORDING_MS;

          if (elapsed < MIN_RECORDING_MS) {
            showSpeechWarning(
              "Please speak for at least a moment before stopping.",
            );
            return;
          }
          if (!hasSpeechDetectedRef.current) {
            showSpeechWarning(
              "No speech detected. Please speak into your microphone.",
            );
            // Throw away the (silent/too-quiet) audio captured so far and
            // start a clean recording. Previously we just `return`ed here,
            // which left the MediaRecorder running — so a quiet false-start
            // got concatenated with the next thing the user said, producing
            // duplicated transcripts (e.g. "Thank you Thank you").
            discardAndRestartRecording();
            recordingStartedAtRef.current = Date.now();
            hasSpeechDetectedRef.current = false;
            return;
          }

          stopAndSendRecording();
          setShowHint(false);
          setWaitingForBotResponse(true);
          waitingForBotResponseRef.current = true;
          clearSpacebarTimer();

          // NEW: Stop video recording when user finishes answering
          if (stopQARecording) {
            setTimeout(() => {
              stopQARecording();
            }, 2000);
          }

          if (speechIntervalRef.current) {
            clearInterval(speechIntervalRef.current);
            speechIntervalRef.current = null;
          }

          return;
        }

        //  USER STARTS SPEAKING
        const now = new Date().toISOString();

        //  speech timestamp (PER QA)

        window.__speechStartTimestamp = now;
        recordingStartedAtRef.current = Date.now();
        hasSpeechDetectedRef.current = false;

        startRecording();
        setShowHint(true);
        setShowPressSpacebarWarning(false);

        // 🧹 Stop repeating “no spacebar pressed” reminders
        clearSpacebarTimer();

        // 🧹 Clear any existing silence detection interval
        if (speechIntervalRef.current) {
          clearInterval(speechIntervalRef.current);
          speechIntervalRef.current = null;
        }

        // --- Silence Detection Logic ---
        // Use pre-calibrated baseline; if not yet available fall back to MIN_SPEECH_THRESHOLD
        const getSpeakingThreshold = () =>
          Math.max(
            (speechBaselineRef.current ?? 0) + SPEECH_MARGIN,
            MIN_SPEECH_THRESHOLD,
          );
        const SILENCE_CONFIRM_DURATION = 3000; // must be silent 3s continuously
        const SILENCE_NOTIFY_INTERVAL = 30000; // alert every 30s of silence
        const CHECK_INTERVAL = 500;
        const SMOOTH_WINDOW = 10;

        const levelHistory = [];
        let consecutiveSilenceStart = null;
        let lastNotifyTime = 0;

        speechIntervalRef.current = setInterval(() => {
          const lvl = readRawRMS();

          // Maintain rolling average
          levelHistory.push(lvl);
          if (levelHistory.length > SMOOTH_WINDOW) levelHistory.shift();
          const avgLevel =
            levelHistory.reduce((a, b) => a + b, 0) / levelHistory.length;

          //  User speaking → reset silence tracking
          if (avgLevel > getSpeakingThreshold()) {
            if (consecutiveSilenceStart !== null) {
            }
            consecutiveSilenceStart = null;
            lastNotifyTime = 0;
            return;
          }

          //  User is silent
          if (consecutiveSilenceStart === null) {
            consecutiveSilenceStart = Date.now();
          }

          const currentSilenceDuration = Date.now() - consecutiveSilenceStart;

          // Require 3s continuous silence before counting
          if (currentSilenceDuration < SILENCE_CONFIRM_DURATION) return;

          const periodsPassed = Math.floor(
            currentSilenceDuration / SILENCE_NOTIFY_INTERVAL,
          );
          const lastPeriod = Math.floor(
            lastNotifyTime / SILENCE_NOTIFY_INTERVAL,
          );

          // Trigger once per 30s of silence
          if (periodsPassed > lastPeriod) {
            // Only notify if the user is still in recording mode (hasn't submitted yet)
            if (isRecordingRef.current) {
              //setShowHint(false);
              //notifyCandidateNotSpeaking();
            }
            lastNotifyTime = currentSilenceDuration;
          }
        }, CHECK_INTERVAL);
      }
    },
    [
      isRecording,
      isReady,
      startRecording,
      stopAndSendRecording,
      isBotBusy,
      waitingForBotResponse,
      botCurrentlySpeaking,
      silentDetected,
      showQuestionnaire,
      isQuestionLoading,
      conversationState,
      isPlayingPreQuestionnaireAudio,
      readRawRMS,
    ],
  );

  useEffect(() => {
    let micDetectInterval = null;

    // Run whenever we're idle and ready — serves two purposes:
    // 1. Pre-calibrate the ambient noise baseline (so speech detector is ready immediately)
    // 2. Show "press spacebar" nudge when mic activity is detected
    if (conversationState === "ready_for_user" && !isRecording && !isBotBusy) {
      const NUDGE_THRESHOLD = 0.1;
      const CHECK_INTERVAL = 100; // 100ms — tight enough for good baseline
      const CALIB_WINDOW = 30; // keep last 30 samples = 3s rolling baseline

      micDetectInterval = setInterval(() => {
        const lvl = readRawRMS();

        // ── Always update the rolling noise baseline ──────────────────────
        noiseCalibSamplesRef.current.push(lvl);
        if (noiseCalibSamplesRef.current.length > CALIB_WINDOW) {
          noiseCalibSamplesRef.current.shift();
        }
        // Baseline = average of quietest 80% of samples (discard loud spikes)
        const sorted = [...noiseCalibSamplesRef.current].sort((a, b) => a - b);
        const quietSamples = sorted.slice(0, Math.ceil(sorted.length * 0.8));
        const newBaseline =
          quietSamples.reduce((a, b) => a + b, 0) / quietSamples.length;
        speechBaselineRef.current = newBaseline;

        // ── Nudge: show "press spacebar" if mic is active ─────────────────
        if (
          showHint &&
          lvl > Math.max(newBaseline + NUDGE_THRESHOLD, 0.08) &&
          !isRecording &&
          conversationState === "ready_for_user"
        ) {
          if (!showPressSpacebarWarning) {
            setShowPressSpacebarWarning(true);
          }
        }
      }, CHECK_INTERVAL);
    } else {
      // Not idle — clear calibration samples so stale data doesn't pollute next session
      noiseCalibSamplesRef.current = [];
    }

    return () => {
      if (micDetectInterval) {
        clearInterval(micDetectInterval);
        micDetectInterval = null;
      }
    };
  }, [
    conversationState,
    isRecording,
    showHint,
    isBotBusy,
    readRawRMS,
    showPressSpacebarWarning,
  ]);

  // keep ref updated whenever audioLevel changes
  useEffect(() => {
    audioLevelRef.current = audioLevel;
  }, [audioLevel]);

  // ─── Direct-analyser speech detector ─────────────────────────────────────
  // Uses the baseline pre-calibrated passively above — no warmup window needed.
  // A grace period (SUSTAIN_GRACE_MS) means brief natural dips in soft speech
  // (between syllables) don't reset the sustain streak.
  useEffect(() => {
    if (isRecording) {
      speechSustainStartRef.current = null;
      let lastAboveThresholdAt = null;

      if (!speechDetectIntervalRef.current) {
        speechDetectIntervalRef.current = setInterval(() => {
          if (audioContextRef?.current?.state === "suspended") {
            audioContextRef.current.resume().catch(() => {});
          }

          const lvl = readRawRMS();

          // Use pre-calibrated baseline; fall back to MIN_SPEECH_THRESHOLD
          // if idle calibration hasn't run yet (e.g. very first recording)
          const baseline = speechBaselineRef.current ?? 0;
          const threshold = Math.max(
            baseline + SPEECH_MARGIN,
            MIN_SPEECH_THRESHOLD,
          );

          if (lvl > threshold) {
            lastAboveThresholdAt = Date.now();
            if (speechSustainStartRef.current === null) {
              speechSustainStartRef.current = Date.now();
            }
          } else if (
            lastAboveThresholdAt !== null &&
            Date.now() - lastAboveThresholdAt < SUSTAIN_GRACE_MS
          ) {
            // Within grace period — treat as still speaking (handles inter-syllable dips)
          } else {
            // Silence beyond grace period — reset streak
            speechSustainStartRef.current = null;
          }

          if (speechSustainStartRef.current !== null) {
            const sustainedFor = Date.now() - speechSustainStartRef.current;
            if (sustainedFor >= SUSTAINED_MS) {
              hasSpeechDetectedRef.current = true;
              clearInterval(speechDetectIntervalRef.current);
              speechDetectIntervalRef.current = null;
            }
          }
        }, 50);
      }
    } else {
      if (speechDetectIntervalRef.current) {
        clearInterval(speechDetectIntervalRef.current);
        speechDetectIntervalRef.current = null;
      }
    }
    return () => {
      if (speechDetectIntervalRef.current) {
        clearInterval(speechDetectIntervalRef.current);
        speechDetectIntervalRef.current = null;
      }
    };
  }, [isRecording, readRawRMS, audioContextRef]);

  // ─── NEW LOW VOICE DETECTOR (Continuous Monitoring with Rolling Average) ───
  useEffect(() => {
    if (!isRecording) {
      setShowLowVoiceWarning(false);
      return;
    }

    let lowVoiceStartAt = null;
    let graceStartAt = null;

    const history = [];

    const HISTORY_SIZE = 30; // ~1.5 seconds at 50ms

    // Frequency data scale (0-255).
    // This ignores background static and focuses purely on voice energy.
    const SILENCE_THRESHOLD = 1;

    // Tune this after testing with real users.
    const LOW_VOICE_THRESHOLD = 75;

    // Low voice needs to persist for 5 seconds.
    const LOW_VOICE_DURATION_MS = 5000;

    // Allow temporary increases / pauses.
    const GRACE_PERIOD_MS = 2000;

    const CHECK_INTERVAL_MS = 50;

    const intervalId = setInterval(() => {
      const currentLvl = readVoiceVolume();
      //console.log("currentLvl", currentLvl);
      history.push(currentLvl);

      if (history.length > HISTORY_SIZE) {
        history.shift();
      }

      // Wait until we have enough samples.
      if (history.length < 15) {
        return;
      }

      const avgLvl =
        history.reduce((sum, value) => sum + value, 0) / history.length;

      // console.log(
      //   "[VoiceMonitor]",
      //   "current:",
      //   currentLvl.toFixed(3),
      //   "average:",
      //   avgLvl.toFixed(3),
      // );

      // =========================================================
      // SILENCE
      // =========================================================

      if (avgLvl <= SILENCE_THRESHOLD) {
        if (lowVoiceStartAt !== null) {
          if (graceStartAt === null) {
            graceStartAt = Date.now();
          }

          const graceDuration = Date.now() - graceStartAt;

          if (graceDuration <= GRACE_PERIOD_MS) {
            //console.log("[VoiceMonitor] Silence grace:", `${graceDuration}ms`);

            return;
          }

          //console.log("[VoiceMonitor] Silence exceeded grace - reset");

          lowVoiceStartAt = null;
          graceStartAt = null;

          setShowLowVoiceWarning(false);
        }

        return;
      }

      // =========================================================
      // LOW VOICE
      // =========================================================

      if (avgLvl < LOW_VOICE_THRESHOLD) {
        // We are back in low voice.
        // Cancel any grace period.
        graceStartAt = null;

        // Start the timer.
        if (lowVoiceStartAt === null) {
          lowVoiceStartAt = Date.now();

          //console.log("[VoiceMonitor] Low voice started");
        }

        const duration = Date.now() - lowVoiceStartAt;

        // console.log(
        //   "[VoiceMonitor]",
        //   "LOW VOICE duration:",
        //   `${(duration / 1000).toFixed(1)}s`,
        // );

        // =======================================================
        // 5 SECOND WARNING
        // =======================================================

        if (duration >= LOW_VOICE_DURATION_MS) {
          setShowLowVoiceWarning(true);

          //console.log("[VoiceMonitor] ⚠️ LOW VOICE WARNING");
        }

        return;
      }

      // =========================================================
      // ABOVE LOW-VOICE THRESHOLD
      // =========================================================

      if (lowVoiceStartAt !== null) {
        if (graceStartAt === null) {
          graceStartAt = Date.now();

          // console.log(
          //   "[VoiceMonitor]",
          //   "Voice temporarily recovered - grace started",
          // );
        }

        const graceDuration = Date.now() - graceStartAt;

        if (graceDuration <= GRACE_PERIOD_MS) {
          // console.log(
          //   "[VoiceMonitor]",
          //   "Recovery grace:",
          //   `${graceDuration}ms`,
          // );

          return;
        }

        // Voice has been sufficiently high for > 1 second.
        //console.log("[VoiceMonitor]", "Voice recovered - resetting streak");

        lowVoiceStartAt = null;
        graceStartAt = null;

        setShowLowVoiceWarning(false);

        return;
      }

      // No active low voice streak.
      graceStartAt = null;
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);

      setShowLowVoiceWarning(false);
    };
  }, [isRecording, readVoiceVolume]);

  useEffect(() => {
    window.addEventListener("keydown", handleSpacebarPress);
    return () => window.removeEventListener("keydown", handleSpacebarPress);
  }, [handleSpacebarPress]);

  // Handle questionnaire answer submission - UPDATED with API call
  const handleQuestionnaireAnswer = async (answers, draggedItems) => {
    let speedToAnswer = null;
    if (questionStartTime) {
      speedToAnswer = (Date.now() - questionStartTime) / 1000; // seconds float
    }

    if (currentQuestion) {
      markQuestionAsUsed(currentQuestion.id);

      // Submit answer to API
      const success = await submitAnswerToAPI(
        currentQuestion,
        answers,
        draggedItems,
        speedToAnswer,
      );

      if (success) {
      } else {
        console.error("Failed to submit answer to API");
      }
    }

    const answerData = {
      questionId: currentQuestion?.id,
      type: currentQuestion?.type,
      answers: answers,
      draggedItems: draggedItems,
      timestamp: new Date().toISOString(),
    };

    window.dispatchEvent(
      new CustomEvent("questionnaire-answered", {
        detail: answerData,
      }),
    );
  };
  const handleQuestionnairePass = ({ questionId, type } = {}) => {
    if (questionId) {
      // Skipped question shouldn't be served again
      markQuestionAsUsed(questionId);
    }

    if (pendingBotResponses.length > 0) {
      return true; // let QuestionnaireComponent close itself
    }

    // No interview response yet — move to the next question ourselves,
    // keep the questionnaire open.
    (async () => {
      setIsQuestionLoading(true);
      try {
        const nextQuestion = await getNextQuestion();
        if (nextQuestion) {
          setCurrentQuestion(nextQuestion);
          setQuestionStartTime(Date.now());
          setQuestionnaireCount((prev) => prev + 1);
        } else {
          // No more questions left in the queue — nothing to show, close for real
          window.dispatchEvent(
            new CustomEvent("mcq-passed", {
              detail: { questionId, type, timestamp: Date.now() },
            }),
          );
          window.dispatchEvent(new CustomEvent("questionnaire-closed"));
        }
      } catch (error) {
        console.error("Error loading next question after Pass:", error);
      } finally {
        setIsQuestionLoading(false);
      }
    })();

    return false; // tell the child not to close itself
  };
  // Cleanup for questionnaire closed event
  useEffect(() => {
    const handleQuestionnaireClosed = () => {
      setShowQuestionnaire(false);
      setCurrentQuestion(null);
      setIsQuestionLoading(false);

      // FIXED: Add small delay before playing pending responses to ensure questionnaire is fully closed
      setTimeout(() => {
        playAllPendingBotResponses();
      }, 100);
    };

    window.addEventListener("questionnaire-closed", handleQuestionnaireClosed);
    return () => {
      window.removeEventListener(
        "questionnaire-closed",
        handleQuestionnaireClosed,
      );
    };
  }, [pendingBotResponses]);

  if (!isReady) return null;
  const getRandomAcknowledgement = () => {
    const phrases = ["Alright!", "Thank you!", "OK…", "Great!"];
    return phrases[Math.floor(Math.random() * phrases.length)];
  };

  // SINGLE SOURCE OF TRUTH: previously the hint text (getHintText/shouldShowHint)
  // and the mic icon (shouldShowMicIcon) each re-implemented their own copy of
  // "are we ready for the user to speak". Because botCurrentlySpeaking/
  // waitingForBotResponse (local state) and isBotBusy (from useBotSpeech, an
  // external hook) don't always update in the same render tick, the two copies
  // could briefly disagree — showing/hiding the mic a beat before or after the
  // text. Computing it once here and reusing it in both places removes that
  // possibility entirely: they now either show together or hide together.
  const isReadyToStartSpeaking =
    !showCountdown &&
    !showQuestionnaire &&
    !silentDetected &&
    !isBotBusy &&
    !botCurrentlySpeaking &&
    !waitingForBotResponse &&
    !isPlayingPreQuestionnaireAudio &&
    messageWaitQueueLength === 0 &&
    showHint &&
    conversationState === "ready_for_user" &&
    !lstReceived &&
    !isRecording;

  const isReadyToStopSpeaking =
    !showCountdown &&
    !showQuestionnaire &&
    !silentDetected &&
    !isBotBusy &&
    !botCurrentlySpeaking &&
    !waitingForBotResponse &&
    !isPlayingPreQuestionnaireAudio &&
    messageWaitQueueLength === 0 &&
    showHint &&
    isRecording;

  // FIXED: Proper hint text logic based on conversation flow
  const getHintText = () => {
    if (isLowBatteryAudioPlaying) return false;
    // Check silent detected - highest priority
    if (silentDetected) {
      return (
        <span
          className="font-normal"
          style={{ paddingLeft: "20px", paddingRight: "20px" }}
        >
          You've been silent for the last 30 seconds. Please wait...
        </span>
      );
    }

    // Check bot speaking states first - highest priority
    if (isBotBusy || botCurrentlySpeaking) {
      const panelDetails = JSON.parse(
        sessionStorage.getItem("panelDetails") || "{}",
      );

      const { panel, pnlf1, pnlf2, pnlm1, pnlm2 } = panelDetails || {};
      const isPnloPanel = panel === "PNLO";

      // Determine name dynamically from sessionStorage
      const storedName = pnlf1 || pnlf2 || pnlm1 || pnlm2 || "Speaker";

      // Prefer current active speaker, else fallback to stored name
      const speakerName = isPnloPanel
        ? storedName
        : activePanelSpeaker || lastKnownSpeaker || storedName;

      return (
        <span
          className="font-normal"
          style={{ paddingLeft: "20px", paddingRight: "20px" }}
        >
          <span className="font-capitalize">
            {speakerName.charAt(0).toUpperCase() + speakerName.slice(1)}
          </span>{" "}
          is speaking….
        </span>
      );
    }

    // Hide hint while waiting for bot response
    if (waitingForBotResponse) return false;

    const getGreeting = () => {
      const hour = new Date().getHours();
      if (hour < 12) return "Good Morning";
      if (hour < 18) return "Good Afternoon";
      return "Good Evening";
    };

    if (isReadyToStopSpeaking) {
      return (
        <p
          style={{
            margin: 0,
            paddingLeft: 20,
            paddingRight: 20,
            fontWeight: 400,
          }}
        >
          Press{" "}
          <span style={{ color: "#E8D015", fontWeight: "bold" }}>SPACEBAR</span>{" "}
          or click on{" "}
          <span style={{ color: "#E8D015", fontWeight: "bold" }}>STOP</span>{" "}
          when done speaking
        </p>
      );
    }

    if (isReadyToStartSpeaking) {
      return (
        <p
          style={{
            margin: 0,
            paddingLeft: 20,
            paddingRight: 20,
            fontWeight: 400,
          }}
        >
          Press{" "}
          <span style={{ color: "#15E835", fontWeight: "bold" }}>SPACEBAR</span>{" "}
          or click on{" "}
          <span style={{ color: "#15E835", fontWeight: "bold" }}>Mic</span> to
          START speaking
        </p>
      );
    }
  };

  // FIXED: Determine when to show hint based on proper flow
  const shouldShowHint = () => {
    if (isLowBatteryAudioPlaying) return false;
    if (showCountdown) return false;
    if (showQuestionnaire) return false;
    if (silentDetected) return true;
    if (isBotBusy || waitingForBotResponse || botCurrentlySpeaking) return true;
    if (!showHint) return false;
    if (waitingForIntroAudio || conversationState === "waiting_for_intro")
      return true;

    // Show when ready for user
    if (
      conversationState === "ready_for_user" &&
      !botCurrentlySpeaking &&
      !waitingForBotResponse &&
      !isPlayingPreQuestionnaireAudio &&
      messageWaitQueueLength === 0
    )
      return true;

    return false;
  };
  const shouldShowMicIcon = isReadyToStartSpeaking || isReadyToStopSpeaking;
  return (
    <>
      {/* FIXED: Show hint based on proper flow logic */}
      <AnimatePresence mode="wait">
        {shouldShowHint() && getHintText() && (
          <motion.div
            key={getHintText()?.props?.children?.toString() || "hint"}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed flex justify-center pointer-events-none z-[99]"
            style={{
              top: "20",
              width: sideBarMode ? contentWidth : "100%",
              left: sideBarMode ? 0 : 0,
              transform: "none",
            }}
          >
            <div
              className="text-white px-12 py-8 rounded-3xl text-base first-level-font space-bar-text flex items-center gap-3"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                maxWidth: "100%",
                width: "100%",
              }}
            >
              {/* Hint text takes full width so alignment stays correct */}
              <div
                className="flex-1 text-center"
                style={{
                  lineHeight: "1.6",
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
              >
                {getHintText()}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {showMuteWarning && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 pointer-events-none z-[100]">
          <div className="bg-red-600 text-white px-6 py-3 rounded-lg text-base font-bold shadow-lg border-2 border-red-400 animate-pulse">
            ⚠️ You are talking while muted! Please unmute your microphone.
          </div>
        </div>
      )}
      {showLowVoiceWarning && (
        <motion.div
          key="low-voice-warning"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed flex justify-center pointer-events-none z-[99]"
          style={{
            top: "20",
            width: sideBarMode ? contentWidth : "100%",
            left: sideBarMode ? 0 : 0,
            transform: "none",
          }}
        >
          <div
            className="text-white px-12 py-8 rounded-3xl text-base first-level-font space-bar-text flex items-center gap-3"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              maxWidth: "100%",
              width: "100%",
            }}
          >
            <div
              className="flex-1 text-center"
              style={{ lineHeight: "1.6", paddingLeft: 0, paddingRight: 0 }}
            >
              <p
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  paddingRight: 20,
                  // fontWeight: 400,
                }}
              >
                <span style={{ color: "#E8D015" }}>🔉 Your voice is low.</span>{" "}
                Please speak louder.
              </p>
            </div>
          </div>
        </motion.div>
      )}
      {showPressSpacebarWarning &&
        !botCurrentlySpeaking &&
        getHintText()?.props?.children?.toString()?.includes("Press") && (
          <motion.div
            key="press-spacebar-warning"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="fixed flex justify-center pointer-events-none z-[100]"
            style={{
              top: "180px",
              width: sideBarMode ? contentWidth : "100%",
              left: 0,
            }}
          >
            <div
              className="fixed z-[999] flex justify-center"
              style={{
                top: "50%",
                transform: "translateY(-50%)",
                width: sideBarMode ? contentWidth : "100%",
                left: 0,
                pointerEvents: "none",
              }}
            >
              <div
                className="flex items-center gap-6 text-white px-10 py-6 rounded-xl first-level-font"
                style={{
                  textAlign: "center",
                  border: "0.5px solid #4f4c4c",
                  background: "rgba(0,0,0,0.85)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  maxWidth: "900px",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                }}
              >
                {/* Warning Icon */}
                <div
                  style={{
                    width: 70,
                    height: 70,
                    fontSize: 40,
                    // borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // background: "rgba(255,255,255,0.08)",
                    flexShrink: 0,
                  }}
                >
                  ⚠️
                  {/* <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 9v4"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M12 17h.01"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                      stroke="white"
                      strokeWidth="2"
                    />
                  </svg> */}
                </div>

                {/* Text */}
                <div style={{ lineHeight: 1.5 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 22,
                      letterSpacing: "1px",
                    }}
                  >
                    You Are Inaudible!
                  </div>

                  <div style={{ fontSize: 20, fontWeight: 500 }}>
                    Press <span style={{ fontWeight: 600 }}>SPACEBAR</span> or
                    click on <span style={{ fontWeight: 600 }}>Mic</span>.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

      {/* Processing Bar */}
      {processorAnimating &&
        !showQuestionnaire &&
        !questionnaireVisible &&
        !isPlayingPreQuestionnaireAudio && (
          <ProcessingOverlay
            contentWidth={contentWidth}
            sideBarMode={sideBarMode}
          />
        )}
      {showTooShortWarning && (
        <motion.div
          key="no-speech-warning"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className="fixed flex justify-center pointer-events-none z-[100]"
          style={{
            top: "280px",
            width: sideBarMode ? contentWidth : "100%",
            left: 0,
          }}
        >
          <div
            className="text-white px-6 py-3 rounded-full text-sm first-level-font text-center"
            style={{
              backgroundColor: "rgba(206, 13, 35, 0.9)",
              maxWidth: "80%",
            }}
          >
            🎙️ {showTooShortWarning}
          </div>
        </motion.div>
      )}

      {showSpacebarLongPressWarning && (
        <motion.div
          key="spacebar-longpress-warning"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className="fixed flex justify-center pointer-events-none z-[100]"
          style={{
            top: "230px",
            width: sideBarMode ? contentWidth : "100%",
            left: 0,
          }}
        >
          <div
            className="text-white px-6 py-3 rounded-full text-sm first-level-font text-center"
            style={{
              backgroundColor: "rgba(229, 170, 98, 0.9)",
              maxWidth: "80%",
            }}
          >
            ⚠️ Don’t hold <span style={{ fontWeight: 700 }}>SPACEBAR</span>.
            Just press once to start, and press again when done.
          </div>
        </motion.div>
      )}

      <div
        className="fixed z-[90]"
        style={{
          bottom: showQuestionnaire ? "20px" : "90px",
          pointerEvents: "none",
          width: sideBarMode ? contentWidth : "100%",
          left: sideBarMode ? 0 : "50%",
          transform: sideBarMode ? "none" : "translateX(-50%)",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div className="pointer-events-auto flex items-center gap-3">
          {shouldShowMicIcon &&
            !showQuestionnaire &&
            !isPlayingPreQuestionnaireAudio && (
              <button
                type="button"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: isRecording
                    ? "2px solid rgba(255,255,255,0.18)"
                    : "none",
                  background: isRecording ? "#CE0D23" : "",
                  boxShadow: isRecording
                    ? "0 12px 30px rgba(0, 0, 0, 0.35)"
                    : "none",
                }}
                onClick={(e) => {
                  // Prevent the button from retaining keyboard focus — otherwise
                  // a later Spacebar press triggers BOTH this onClick (native
                  // button activation) AND the global keydown listener,
                  // double-calling startRecording()/stopAndSendRecording() in
                  // the same tick and corrupting the MediaRecorder state.
                  e.currentTarget.blur();
                  if (!isReady) return;
                  if (isBotBusy) return;
                  if (botCurrentlySpeakingRef.current) return; // sync ref guard
                  if (waitingForBotResponseRef.current) return; // sync ref guard
                  if (
                    showQuestionnaire ||
                    isQuestionLoading ||
                    isPlayingPreQuestionnaireAudio
                  )
                    return;
                  if (
                    conversationState !== "ready_for_user" ||
                    lstReceived ||
                    silentDetected
                  )
                    return;

                  if (isRecording) {
                    // Validate: minimum duration 500ms AND meaningful audio detected
                    const MIN_RECORDING_MS = 500;
                    const elapsed = recordingStartedAtRef.current
                      ? Date.now() - recordingStartedAtRef.current
                      : MIN_RECORDING_MS;

                    if (elapsed < MIN_RECORDING_MS) {
                      showSpeechWarning(
                        "Please speak for at least a moment before stopping.",
                      );
                      return;
                    }
                    if (!hasSpeechDetectedRef.current) {
                      showSpeechWarning(
                        "No speech detected. Please speak into your microphone.",
                      );
                      // Same fix as handleSpacebarPress: discard the stale
                      // low-volume audio and start fresh instead of leaving
                      // the recorder running, which was causing it to merge
                      // with the next utterance and duplicate the transcript.
                      discardAndRestartRecording();
                      recordingStartedAtRef.current = Date.now();
                      hasSpeechDetectedRef.current = false;
                      return;
                    }

                    stopAndSendRecording();
                    setShowHint(false);
                    setWaitingForBotResponse(true);
                    waitingForBotResponseRef.current = true;

                    if (stopQARecording) {
                      setTimeout(() => stopQARecording(), 2000);
                    }

                    if (speechIntervalRef.current) {
                      clearInterval(speechIntervalRef.current);
                      speechIntervalRef.current = null;
                    }
                  } else {
                    const now = new Date().toISOString();
                    window.__speechStartTimestamp = now;

                    recordingStartedAtRef.current = Date.now();
                    hasSpeechDetectedRef.current = false;
                    startRecording();
                    setShowHint(true);
                    setShowPressSpacebarWarning(false);
                    clearSpacebarTimer();
                  }
                }}
                title={isRecording ? "Stop recording" : "Start recording"}
              >
                {isRecording ? (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      background: "#fff",
                      borderRadius: 4,
                    }}
                  />
                ) : (
                  <img
                    src={MicIcon}
                    alt="Mic"
                    style={{ width: 100, height: 100, display: "block" }}
                  />
                )}
              </button>
            )}
          {/* Share Screen button - ALWAYS visible */}
          <button
            type="button"
            title={localScreenShareOn ? "Stop sharing" : "Share screen"}
            onClick={() => {
              toggleScreenShare();
            }}
            className="relative group"
            style={{ width: 70, height: 70 }}
          >
            <img
              src={ShareScreenIcon}
              alt="share"
              style={{
                width: 70,
                height: 70,
                display: "block",
                objectFit: "contain",
                opacity: localScreenShareOn ? 1 : 0.9,
              }}
            />
            <div className="custom-tooltip">
              {localScreenShareOn ? "Stop sharing" : "Share Screen"}
              <span className="custom-tooltip-arrow" />
            </div>
          </button>
          {/*  Sidebar toggle - ALWAYS visible */}
          <button
            type="button"
            onClick={() =>
              setSideBarMode((s) => (s ? null : sideBarModes.CHAT))
            }
            className="relative group"
            style={{ width: 70, height: 70 }}
          >
            <img
              src={sideBarMode ? CloseChatIcon : OpenChatIcon}
              alt=""
              style={{
                width: 70,
                height: 70,
                display: "block",
                objectFit: "contain",
              }}
            />

            {/*  Custom Tooltip */}
            <div className="custom-tooltip">
              {sideBarMode ? "Close Live Transcript" : "Open Live Transcript"}
              <span className="custom-tooltip-arrow" />
            </div>
          </button>
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("request-exit-confirmation"))
            }
            className="relative group flex items-center justify-center"
            style={{
              width: 55,
              height: 55,
              marginLeft: 10,
              borderRadius: 999,
              background: "#CE0D23",
              border: "0.5px solid #fff",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.35)",
            }}
          >
            <EndIcon fillcolor="#fff" width={26} height={26} />
            <div className="custom-tooltip custom-tooltip-left">
              Leave Assessment
              <span className="custom-tooltip-arrow custom-tooltip-arrow-left" />
            </div>
          </button>
        </div>
      </div>

      {/* Questionnaire Component */}
      {questionnaireVisible && currentQuestion && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-[100] ${
            questionnaireAnimating
              ? "questionnaire-slide-in"
              : "questionnaire-slide-out"
          }`}
        >
          <QuestionnaireComponent
            question={currentQuestion}
            onAnswer={handleQuestionnaireAnswer}
            onPass={handleQuestionnairePass}
            showResult={true}
          />
        </div>
      )}
    </>
  );
};

export default UserVoiceRecorder;
